import { FunctionComponent, useCallback, useState, useEffect } from 'react'
import { MdCheck, MdSend } from 'react-icons/md'
import { useLocation, useHistory } from 'react-router-dom'

import Button from 'shared/components/Button'
import Modal from 'shared/components/Modal'
import { validateTypeFile } from 'shared/utils/validates'
import UserRepository from '../../infra/repositories/UserRepository'

import iconPhone from 'shared/assets/icons/icon-telefone.png'
import cnh from 'shared/assets/images/cnh.jpeg'
import rg from 'shared/assets/images/rg.png'
import InputFile from 'shared/components/InputFile'
import Page from 'shared/components/Page'
import { colors } from 'shared/styles/colors'
import {
  Container,
  Cnh,
  ContainerCenter,
  ContainerDoc,
  ContainerInput,
  ContainerModal,
  ContainerPhoto,
  Description,
  Rg,
  StyledButton,
  Title,
} from './styles'

interface IResendPhotoState {
  protocolo_ocr: string | null
  needResend: boolean | null
  loading: boolean
  resending: boolean
}

const userRepository = new UserRepository()

const ResendPhoto: FunctionComponent = () => {
  const location = useLocation()
  const [photoOk, setPhotoOk] = useState(false)
  const [file, setFile] = useState('')
  const [photoBase64, setPhotoBase64] = useState('')
  const [openModal, setOpenModal] = useState(true)
  const [isWrongExtension, setIsWrongExtension] = useState(false)
  const [resended, setResended] = useState(false)
  const [state, setState] = useState<IResendPhotoState>({
    protocolo_ocr: null,
    needResend: null,
    loading: true,
    resending: false,
  })

  const history = useHistory()

  // Função para extrair protocolo_ocr da query string
  const getProtocoloOcrFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(location.search)
    return urlParams.get('protocolo_ocr')
  }

  // Função para verificar se precisa reenviar documento
  const needResendDocument = useCallback(async (protocolo: string) => {
    try {
      const response = await userRepository.needResendDocument(protocolo)
      return response.needResend
    } catch (error) {
      console.error('Erro ao verificar se precisa reenviar documento:', error)
      return false
    }
  }, [])

  // Effect para verificar protocolo na inicialização
  useEffect(() => {
    const protocolo_ocr = getProtocoloOcrFromUrl()

    if (!protocolo_ocr) {
      setState(prev => ({
        ...prev,
        protocolo_ocr: null,
        needResend: false,
        loading: false,
        resending: false,
      }))
      return
    }

    const checkProtocolo = async () => {
      const needResend = await needResendDocument(protocolo_ocr)
      setState(prev => ({
        ...prev,
        protocolo_ocr: protocolo_ocr,
        needResend: needResend,
        loading: false,
        resending: false,
      }))
    }

    checkProtocolo()
  }, [location.search, needResendDocument])

  const handleSelectFile = useCallback((base64: string, fileName: string) => {
    setFile(fileName)
    setPhotoBase64(base64)
    if (validateTypeFile(fileName)) {
      setIsWrongExtension(false)
      setPhotoOk(true)
    } else {
      setIsWrongExtension(true)
      setPhotoOk(false)
    }
  }, [])

  const handleResendDocument = useCallback(async () => {
    if (!photoOk || !state.protocolo_ocr || !photoBase64 || state.resending)
      return

    setState(prev => ({ ...prev, resending: true }))

    try {
      await userRepository.resendDocument({
        old_protocolo_ocr: state.protocolo_ocr,
        doc_imagem_frente: photoBase64,
        doc_imagem_verso: photoBase64,
      })

      setResended(true)
      alert('Documento reenviado com sucesso!')
    } catch (error) {
      console.error('Erro ao reenviar documento:', error)
      alert('Erro ao reenviar documento. Tente novamente.')
    } finally {
      setState(prev => ({ ...prev, resending: false }))
    }
  }, [photoOk, state.protocolo_ocr, photoBase64, state.resending])

  if (state.loading) {
    return (
      <Page icon={iconPhone} title="Carregando...">
        <Container>
          <ContainerCenter>
            <div>Verificando protocolo...</div>
          </ContainerCenter>
        </Container>
      </Page>
    )
  }

  if (!state.protocolo_ocr) {
    return (
      <Page icon={iconPhone} title="Protocolo não encontrado">
        <Container>
          <ContainerCenter>
            <div>
              Por favor, acesse esta página através do link fornecido via email.
            </div>
          </ContainerCenter>
        </Container>
      </Page>
    )
  }

  if (state.needResend === false) {
    return (
      <Page icon={iconPhone} title="Reenvio de documentação">
        <Container>
          <ContainerCenter>
            <div>Usuário já criado ou com documentação válida.</div>
          </ContainerCenter>
        </Container>
      </Page>
    )
  }

  return (
    <>
      <Modal
        size={'large'}
        showCloseButton={true}
        openModal={openModal}
        onCloseModal={() => {
          setOpenModal(false)
        }}
      >
        <ContainerModal>
          <Title>Atenção!</Title>

          <Description>
            Seu documento precisa estar aberto para validarmos frente e verso,
            como no exemplo a seguir:
          </Description>

          <ContainerDoc>
            <ContainerPhoto>
              <Rg src={rg} />
              <Title>RG</Title>
            </ContainerPhoto>
            <ContainerPhoto>
              <Cnh src={cnh} />
              <Title>CNH</Title>
            </ContainerPhoto>
          </ContainerDoc>

          <Button
            color={colors.orange}
            onClick={() => {
              setOpenModal(false)
            }}
          >
            <MdCheck /> Ok
          </Button>
        </ContainerModal>
      </Modal>
      <Page
        icon={iconPhone}
        title={
          resended
            ? 'Documento reenviado com sucesso. Aguarde o resultado da análise por email.'
            : 'Reenvio de Documentação<br />Envie uma nova imagem do seu RG ou CNH (frente e verso) para análise:'
        }
      >
        <Container>
          {!resended && (
            <>
              <ContainerInput>
                <InputFile
                  id="photo-resend"
                  onSelectFile={handleSelectFile}
                  file={file}
                  isWrongExtension={isWrongExtension}
                />
              </ContainerInput>
              <ContainerCenter>
                <StyledButton
                  color={photoOk ? colors.green : colors.grayLight}
                  disabled={!photoOk || state.resending}
                  onClick={handleResendDocument}
                  loading={state.resending}
                >
                  <MdSend /> {state.resending ? 'Reenviando...' : 'Reenviar'}
                </StyledButton>
              </ContainerCenter>
            </>
          )}
        </Container>
      </Page>
    </>
  )
}

export default ResendPhoto
